import React, { useMemo } from 'react';
import clsx from 'clsx';

const SizeSelector = ({ product, handleVariantChange, currentVariant, inline = false, placement }) => {
  const productOptions =
    product?.optionsWithValues !== undefined
      ? product?.optionsWithValues
      : product?.variantBySelectedOptions?.selectedOptions;

  const sizeOptionIndex = productOptions?.findIndex((o) => o.name.toLowerCase().includes('size'));
  if (sizeOptionIndex < 0 || product.hasOnlyDefaultVariant) return false;

  const option = useMemo(() => productOptions[sizeOptionIndex], [productOptions, sizeOptionIndex]);

  const updateCurrentVariant = (value) => {
    const updatedOptions = currentVariant.selectedOptions.map((o) => {
      return o.name === option.name ? { name: option.name, value } : o;
    });

    const updatedVariant = product.variants.find(
      (v) => JSON.stringify(v.selectedOptions) === JSON.stringify(updatedOptions),
    );

    handleVariantChange(updatedVariant.id);
  };

  const optionValues = useMemo(() => {
    const colorOption = currentVariant?.selectedOptions?.find((o) => o.name.toLowerCase().includes('color'));
    const variantSizes = product.variants
      .filter((variant) => {
        if (colorOption) {
          return variant.selectedOptions.some((o) => o.value === colorOption.value && o.name === colorOption.name);
        }
        return true;
      })
      .map((variant) => variant.selectedOptions.find((o) => o.name.toLowerCase().includes('size')).value);

    return option.values.filter((value) => {
      return variantSizes.includes(value);
    });
  }, [currentVariant, product.variants, option]);

  return (
    <div
      className={clsx('flex w-full flex-wrap gap-2', {
        'justify-start': inline,
        'justify-center': !inline,
      })}
    >
      {optionValues.map((value) => {
        const selectedOptions = currentVariant.selectedOptions.map((o) =>
          o.name === option.name ? { name: option.name, value } : o,
        );
        const newVariant = product.variants.find(
          (v) => JSON.stringify(v.selectedOptions) === JSON.stringify(selectedOptions),
        );
        const isAvailable = newVariant && newVariant.available && newVariant.inventoryQuantity > 10;

        return (
          <div
            key={`size-${value}`}
            className="flex items-center"
          >
            <label
              htmlFor={`${product.id}-${option.name}-${value.replace(' ', '')}-${placement}`}
              className={`relative cursor-pointer`}
            >
              <input
                type="radio"
                id={`${product.id}-${option.name}-${value.replace(' ', '')}-${placement}`}
                value={value}
                name={product.title}
                tabIndex={-1}
                onChange={() => updateCurrentVariant(value)}
                className={clsx('peer sr-only', { unavailable: !isAvailable })}
                disabled={!isAvailable}
                aria-disabled={!isAvailable}
              />
              <span
                tabIndex={0}
                className={clsx(
                  `caption relative flex size-7.5 cursor-pointer items-center 
                  justify-center rounded-full border bg-white text-center
                  uppercase text-default transition-colors duration-200
                  before:absolute before:-top-px before:hidden before:h-7.5
                  before:w-[0.75px] before:rotate-45 before:bg-grey-dark
                  hover:border-default peer-checked:border-default
                  peer-checked:bg-grey-light peer-disabled:cursor-not-allowed
                  peer-disabled:bg-grey-light peer-disabled:text-grey-dark
                  peer-disabled:before:flex`,
                  {
                    'border-white peer-disabled:hover:border-white': !inline,
                    'border-grey-medium peer-disabled:hover:border-grey-medium': inline,
                  },
                )}
                onKeyDown={({ code }) => (code === 'Enter' || code === 'Space') && updateCurrentVariant(value)}
              >
                {value}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
};
export default SizeSelector;
